import { Avatar, Dropdown } from 'antd';
import type { MenuProps } from 'antd';
import classNames from 'classnames';
import type { ReactElement } from 'react';
import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import logoMitgoIdUrl from '../../assets/icons/logoMitgoID.svg?url';
import { LOCALE, LOCALE_SUPPORTED, localeNormalize, type TLocalesValues } from '../../constants/locale';
import { ROUTE_HOME, ROUTE_LOGOUT } from '../../routes/routes.constants';
import { selectProfileInitials, useProfileState } from '../../store';

import styles from './Header.module.scss';

type THeaderType = 'form' | 'page';

type HeaderProps = {
  type: THeaderType;
  user: boolean;
  fixed?: boolean;
  callback?: () => void;
  clickable?: boolean;
  className?: string;
};

type UserMenuKey = 'logout';

const LANGUAGE_LABELS: Record<TLocalesValues, string> = {
  [LOCALE.ru]: 'Русский',
  [LOCALE.en]: 'English',
  [LOCALE.de]: 'Deutsch',
  [LOCALE.es]: 'Español',
  [LOCALE.pl]: 'Polski',
  [LOCALE.pt]: 'Português',
  [LOCALE.cs]: 'Čeština',
  [LOCALE.uk]: 'Українська',
};

/**
 * Перемещает пользователя по внутренним маршрутам survey-client, сохраняя query и hash.
 *
 * @param {ReturnType<typeof useNavigate>} navigate - router navigate из React Router
 * @param {string} pathname - целевой путь внутри приложения
 * @param {string} search - текущая query-строка
 * @param {string} hash - текущий hash
 * @returns {void}
 */
function navigatePreservingLocation(
  navigate: ReturnType<typeof useNavigate>,
  pathname: string,
  search: string,
  hash: string,
): void {
  navigate({
    pathname,
    search,
    hash,
  });
}

/**
 * Шапка survey-client, визуально и поведенчески повторяющая `passport-client` Header
 * без привязки к passport-specific config layer.
 *
 * @param {HeaderProps} props - параметры отображения шапки
 * @returns {ReactElement}
 */
function Header({ type, user, fixed = false, callback, clickable = true, className }: HeaderProps): ReactElement {
  const { t, i18n } = useTranslation();
  const initials = useProfileState(selectProfileInitials);
  const location = useLocation();
  const navigate = useNavigate();

  const currentLocale = localeNormalize(i18n.language);

  const profileMenuItems: MenuProps['items'] = useMemo(
    () => [{ key: 'logout', danger: true, label: t('shell.logout') }],
    [t],
  );

  const languageMenuItems: MenuProps['items'] = useMemo(
    () =>
      LOCALE_SUPPORTED.map((locale) => ({
        key: locale,
        disabled: locale === currentLocale,
        label: LANGUAGE_LABELS[locale],
      })),
    [currentLocale],
  );

  const userAvatarText = useMemo(() => {
    if (initials !== null && initials !== '') {
      return initials;
    }
    const fallback = t('shell.userFallback');
    if (fallback === '') {
      return 'U';
    }
    return fallback[0].toUpperCase();
  }, [initials, t]);

  /**
   * Обрабатывает клик по логотипу и возвращает пользователя на корневой маршрут survey-client.
   *
   * @returns {void}
   */
  const logoClick = useCallback(() => {
    if (!clickable) {
      return;
    }
    if (typeof callback === 'function') {
      callback();
    }
    navigatePreservingLocation(navigate, ROUTE_HOME, location.search, location.hash);
  }, [callback, clickable, location.hash, location.search, navigate]);

  /**
   * Переключает активный язык интерфейса survey-client.
   *
   * @param {string} locale - выбранный language tag
   * @returns {void}
   */
  const changeLanguage = useCallback(
    (locale: string) => {
      i18n.changeLanguage(locale).catch((error: unknown) => {
        const errorMessage = error instanceof Error ? error.message : String(error);
        console.error('[survey-client] failed to change language', errorMessage);
      });
    },
    [i18n],
  );

  /**
   * Выполняет действия user-menu профиля.
   *
   * @param {UserMenuKey} action - выбранный пункт меню
   * @returns {void}
   */
  const handleUserMenuAction = useCallback(
    (action: UserMenuKey) => {
      if (action !== 'logout') {
        return;
      }
      navigate({ pathname: ROUTE_LOGOUT, search: location.search });
    },
    [location.search, navigate],
  );

  const classWrapper = classNames(
    styles.this_wrapper,
    {
      [styles.this_wrapper__fixed]: fixed,
      [styles.this_wrapper__form]: type === 'form',
    },
    className,
  );

  const classLogo = classNames(styles.this_logo, {
    [styles.this_logo__clickable]: clickable,
  });

  return (
    <div className={classWrapper}>
      <div className={styles.this_container}>
        <button type='button' className={classLogo} onClick={logoClick}>
          <img className={styles.common_iconMargin__s} src={logoMitgoIdUrl} alt='Mitgo ID' />
        </button>
        <div className={styles.this_menu}>
          {LOCALE_SUPPORTED.length > 1 && (
            <Dropdown
              trigger={['click']}
              overlayClassName={styles.this_dropdown}
              menu={{
                items: languageMenuItems,
                onClick: ({ key }) => {
                  if (typeof key === 'string' && key !== '') {
                    changeLanguage(key);
                  }
                },
              }}
            >
              <button type='button' className={styles.this_languageTrigger}>
                <span>{currentLocale.toUpperCase()}</span>
                <span className={styles.this_caret}>▾</span>
              </button>
            </Dropdown>
          )}
          {user && (
            <Dropdown
              trigger={['click']}
              overlayClassName={styles.this_dropdown}
              menu={{
                items: profileMenuItems,
                onClick: ({ key }) => {
                  if (key === 'logout') {
                    handleUserMenuAction(key);
                  }
                },
              }}
            >
              <button type='button' className={styles.this_userTrigger}>
                <Avatar size={38} className={styles.this_avatar}>
                  {userAvatarText}
                </Avatar>
              </button>
            </Dropdown>
          )}
        </div>
      </div>
    </div>
  );
}

export default memo(Header);
